import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { backupNow, radiusStatus, reloadRadius, restartRadius } from "@/api/endpoints/settings";
import type { CommandResult } from "@/api/endpoints/settings";
import { useAuthStore } from "@/store/authStore";

function ResultPanel({ result }: { result: CommandResult }) {
  const ok = result.exit_code === 0;
  return (
    <div className="mt-4 rounded-md border border-border bg-surface-2 p-4 font-mono text-sm">
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${ok ? "bg-success" : "bg-critical"}`}
        />
        <span className="text-text-muted">
          {result.operation} — exit {result.exit_code} · {result.duration_ms}ms
        </span>
      </div>
      {result.stdout && <pre className="whitespace-pre-wrap text-text">{result.stdout}</pre>}
      {result.stderr && <pre className="whitespace-pre-wrap text-critical">{result.stderr}</pre>}
    </div>
  );
}

export function SettingsPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canControlService = hasPermission("settings.service_control");
  const canBackupRestore = hasPermission("settings.backup_restore");
  const [lastResult, setLastResult] = useState<CommandResult | null>(null);

  const runMutation = useMutation({
    mutationFn: (fn: () => Promise<CommandResult>) => fn(),
    onSuccess: (result) => {
      setLastResult(result);
      toast(result.exit_code === 0 ? "Command succeeded" : "Command failed", {
        description: `${result.operation} · exit ${result.exit_code}`,
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-lg border border-border bg-surface p-6">
        <h2 className="mb-1 text-lg font-semibold text-text">FreeRADIUS service</h2>
        <p className="mb-4 text-sm text-text-muted">
          Runs through the whitelisted CommandExecutor (see design doc §05) — every call is
          audit-logged with actor, exit code, and duration.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-md border border-border bg-surface-2 px-4 py-2 text-sm text-text hover:border-accent"
            onClick={() => runMutation.mutate(radiusStatus)}
          >
            Check status
          </button>
          <button
            disabled={!canControlService}
            className="rounded-md border border-border bg-surface-2 px-4 py-2 text-sm text-text hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => runMutation.mutate(reloadRadius)}
          >
            Reload
          </button>
          <button
            disabled={!canControlService}
            className="rounded-md border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning hover:border-warning disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => {
              if (window.confirm("Restart FreeRADIUS now? Active auth requests will briefly fail.")) {
                runMutation.mutate(restartRadius);
              }
            }}
          >
            Restart
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface p-6">
        <h2 className="mb-1 text-lg font-semibold text-text">Backup</h2>
        <p className="mb-4 text-sm text-text-muted">
          Restore is deliberately not wired up in the UI yet — Stage 2.6 adds a backup browser
          with a typed confirmation step before it's exposed.
        </p>
        <button
          disabled={!canBackupRestore}
          className="rounded-md border border-border bg-surface-2 px-4 py-2 text-sm text-text hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => runMutation.mutate(backupNow)}
        >
          Backup now
        </button>
      </div>

      {lastResult && <ResultPanel result={lastResult} />}
    </div>
  );
}
